import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
  Assignment as AssignIcon,
  CheckCircle as ResolveIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";

import Page from "./Page";
import useApi from "../../hooks/useApi";
import Dialog_YesNo from "../ui/Dialog_YesNo";

const ErrorLogs = () => {
  const { getData, postData, deleteData } = useApi();

  const [activeTab, setActiveTab] = useState(0);
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [errorToResolve, setErrorToResolve] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [errorToDelete, setErrorToDelete] = useState(null);
  const [newTag, setNewTag] = useState("");
  const [newNote, setNewNote] = useState("");

  useEffect(() => {
    loadData();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Get error statistics
      const statsResponse = await getData({
        entityName: "Log",
        action: "getErrorStats",
      });
      if (statsResponse.isSuccessful) {
        setStats(statsResponse.data);
      }

      // Get errors based on active tab
      const filters = {};
      if (activeTab === 1) filters.severity = "critical";
      if (activeTab === 2) filters.severity = "high";
      if (activeTab === 3) filters.status = "new";
      if (activeTab === 4) {
        filters.status = "resolved";
      } else {
        // Exclude resolved errors from all other tabs
        filters.excludeStatus = "resolved";
      }

      const errorsResponse = await getData({
        entityName: "Log",
        action: "getGroupedErrors",
        filter: filters,
      });
      if (errorsResponse.isSuccessful) {
        setErrors(errorsResponse.data || []);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getData({
        entityName: "User",
        action: "get",
      });
      if (response.isSuccessful) {
        // Filter to admin users with canAccessErrorLogs permission
        const adminUsers = (response.data || []).filter(
          (u) => u.permissions?.canAccessErrorLogs || u.role === "admin",
        );
        setUsers(adminUsers);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case "critical":
        return "error";
      case "high":
        return "warning";
      case "medium":
        return "info";
      case "info":
        return "info";
      case "low":
        return "default";
      default:
        return "default";
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case "critical":
      case "high":
        return <ErrorIcon fontSize="small" />;
      case "medium":
        return <WarningIcon fontSize="small" />;
      case "info":
        return <InfoIcon fontSize="small" />;
      case "low":
        return <InfoIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const handleViewDetails = async (error) => {
    try {
      const response = await getData({
        entityName: "Log",
        action: "getById",
        id: error.logId,
      });
      if (response.isSuccessful) {
        setSelectedError(response.data);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error("Error loading details:", error);
    }
  };

  const handleAssignError = (error) => {
    setSelectedError(error);
    setAssignDialogOpen(true);
  };

  const submitAssignment = async () => {
    if (!selectedUser || !selectedError) return;

    try {
      const response = await postData({
        entityName: "Log",
        action: "assignError",
        data: {
          logId: selectedError.logId,
          userId: selectedUser,
        },
      });

      if (response.isSuccessful) {
        setAssignDialogOpen(false);
        setSelectedUser("");
        loadData();
      }
    } catch (error) {
      console.error("Error assigning:", error);
    }
  };

  const handleResolveError = (error) => {
    setErrorToResolve(error);
    setResolveDialogOpen(true);
  };

  const confirmResolveError = async () => {
    if (!errorToResolve) return;

    try {
      const response = await postData({
        entityName: "Log",
        action: "updateStatus",
        data: {
          logId: errorToResolve.logId,
          status: "resolved",
          note: "Resolved from error logs dashboard",
        },
      });

      if (response.isSuccessful) {
        setResolveDialogOpen(false);
        setErrorToResolve(null);
        loadData();
      }
    } catch (error) {
      console.error("Error resolving:", error);
    }
  };

  const handleDeleteError = (error) => {
    setErrorToDelete(error);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteError = async () => {
    if (!errorToDelete?.logId) return;

    try {
      const response = await deleteData({
        entityName: "Log",
        id: errorToDelete.logId,
      });

      if (response?.isSuccessful !== false) {
        setDeleteDialogOpen(false);
        setErrorToDelete(null);
        loadData();
      }
    } catch (error) {
      console.error("Error deleting log:", error);
    }
  };

  const handleAddTag = async () => {
    if (!newTag.trim() || !selectedError) return;

    try {
      const response = await postData({
        entityName: "Log",
        action: "addTag",
        data: {
          logId: selectedError._id,
          tag: newTag.trim(),
        },
      });

      if (response.isSuccessful) {
        setNewTag("");
        // Refresh the selected error
        const detailsResponse = await getData({
          entityName: "Log",
          action: "getById",
          id: selectedError._id,
        });
        if (detailsResponse.isSuccessful) {
          setSelectedError(detailsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error adding tag:", error);
    }
  };

  const handleRemoveTag = async (tag) => {
    if (!selectedError) return;

    try {
      const response = await postData({
        entityName: "Log",
        action: "removeTag",
        data: {
          logId: selectedError._id,
          tag: tag,
        },
      });

      if (response.isSuccessful) {
        // Refresh the selected error
        const detailsResponse = await getData({
          entityName: "Log",
          action: "getById",
          id: selectedError._id,
        });
        if (detailsResponse.isSuccessful) {
          setSelectedError(detailsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error removing tag:", error);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedError) return;

    try {
      const response = await postData({
        entityName: "Log",
        action: "addNote",
        data: {
          logId: selectedError._id,
          note: newNote.trim(),
        },
      });

      if (response.isSuccessful) {
        setNewNote("");
        // Refresh the selected error
        const detailsResponse = await getData({
          entityName: "Log",
          action: "getById",
          id: selectedError._id,
        });
        if (detailsResponse.isSuccessful) {
          setSelectedError(detailsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const copyJsonToClipboard = async (value) => {
    if (value === null || value === undefined) return;

    const text = JSON.stringify(value, null, 2);

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return;
      }
    } catch (err) {
      // Fallback below if Clipboard API fails.
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    } catch (err) {
      console.error("Error copying JSON to clipboard:", err);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString();
  };

  return (
    <Page pageName="error-logs">
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            component="h1"
          >
            Error Logs
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadData}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Statistics Cards */}
        {stats && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 2,
              mb: 3,
            }}
          >
            <Card>
              <CardContent>
                <Typography
                  color="textSecondary"
                  gutterBottom
                >
                  Total Errors
                </Typography>
                <Typography variant="h4">{stats.total}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: "#ffebee" }}>
              <CardContent>
                <Typography
                  color="textSecondary"
                  gutterBottom
                >
                  Critical
                </Typography>
                <Typography variant="h4">
                  {stats.bySeverity.critical}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: "#fff3e0" }}>
              <CardContent>
                <Typography
                  color="textSecondary"
                  gutterBottom
                >
                  High Priority
                </Typography>
                <Typography variant="h4">{stats.bySeverity.high}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ bgcolor: "#e3f2fd" }}>
              <CardContent>
                <Typography
                  color="textSecondary"
                  gutterBottom
                >
                  Unresolved
                </Typography>
                <Typography variant="h4">
                  {stats.byStatus.new + stats.byStatus.acknowledged}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Tabs */}
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
          >
            <Tab label="All Errors" />
            <Tab label="Critical" />
            <Tab label="High Priority" />
            <Tab label="New" />
            <Tab label="Resolved" />
          </Tabs>
        </Paper>

        {/* Error Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="center">Occurrences</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {errors.map((error) => (
                <TableRow
                  key={error._id}
                  sx={{
                    bgcolor:
                      error.severity === "critical"
                        ? "#ffebee"
                        : error.severity === "high"
                          ? "#fff3e0"
                          : "inherit",
                  }}
                >
                  <TableCell>
                    <Chip
                      icon={getSeverityIcon(error.severity)}
                      label={error.severity}
                      color={getSeverityColor(error.severity)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {error.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {error.source && typeof error.source === "object"
                        ? error.source.platform || JSON.stringify(error.source)
                        : error.source}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={error.occurrenceCount}
                      size="small"
                      color={error.occurrenceCount > 10 ? "error" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(error.lastOccurrence)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={error.status}
                      size="small"
                      color={error.status === "new" ? "warning" : "default"}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetails(error)}
                      title="View Details"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleAssignError(error)}
                      title="Assign"
                    >
                      <AssignIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleResolveError(error)}
                      title="Resolve"
                    >
                      <ResolveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteError(error)}
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {errors.length === 0 && !loading && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ py: 4 }}
                    >
                      No errors found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Details Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Error Details</DialogTitle>
          <DialogContent>
            {selectedError && (
              <Box>
                <Typography variant="subtitle2">Message:</Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {selectedError.name || selectedError.message}
                </Typography>

                <Typography variant="subtitle2">Source:</Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {selectedError.source &&
                  typeof selectedError.source === "object"
                    ? selectedError.source.platform ||
                      JSON.stringify(selectedError.source)
                    : selectedError.source}
                </Typography>

                <Typography variant="subtitle2">Severity:</Typography>
                <Chip
                  label={selectedError.severity}
                  color={getSeverityColor(selectedError.severity)}
                  size="small"
                  sx={{ mb: 2 }}
                />

                {selectedError.stackTrace && (
                  <>
                    <Typography variant="subtitle2">Stack Trace:</Typography>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f5f5f5",
                        maxHeight: "300px",
                        overflow: "auto",
                        mb: 2,
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: "12px" }}>
                        {selectedError.stackTrace}
                      </pre>
                    </Paper>
                  </>
                )}

                {selectedError.data && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">Data:</Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyJsonToClipboard(selectedError.data)}
                        title="Copy Data JSON"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f5f5f5",
                        maxHeight: "300px",
                        overflow: "auto",
                        mb: 2,
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: "12px" }}>
                        {JSON.stringify(selectedError.data, null, 2)}
                      </pre>
                    </Paper>
                  </>
                )}

                {selectedError.details && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">Details:</Typography>
                      <IconButton
                        size="small"
                        onClick={() =>
                          copyJsonToClipboard(selectedError.details)
                        }
                        title="Copy Details JSON"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Paper
                      sx={{
                        p: 2,
                        bgcolor: "#f5f5f5",
                        maxHeight: "240px",
                        overflow: "auto",
                        mb: 2,
                      }}
                    >
                      <pre style={{ margin: 0, fontSize: "12px" }}>
                        {JSON.stringify(selectedError.details, null, 2)}
                      </pre>
                    </Paper>
                  </>
                )}

                <Typography variant="subtitle2">Occurrence Count:</Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {selectedError.occurrenceCount}
                </Typography>

                <Typography variant="subtitle2">First Seen:</Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {formatDate(selectedError.timestamp)}
                </Typography>

                <Typography variant="subtitle2">Last Seen:</Typography>
                <Typography
                  variant="body2"
                  gutterBottom
                  sx={{ mb: 2 }}
                >
                  {formatDate(selectedError.lastOccurrence)}
                </Typography>

                {/* Tags Section */}
                <Typography variant="subtitle2">Tags:</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  {selectedError.tags && selectedError.tags.length > 0 ? (
                    selectedError.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        size="small"
                        onDelete={() => handleRemoveTag(tag)}
                      />
                    ))
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      No tags
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Add Tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleAddTag();
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddTag}
                    disabled={!newTag.trim()}
                  >
                    Add
                  </Button>
                </Box>

                {/* Notes Section */}
                <Typography variant="subtitle2">Notes:</Typography>
                {selectedError.notes && selectedError.notes.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    {selectedError.notes.map((note, index) => (
                      <Paper
                        key={index}
                        sx={{ p: 1, mb: 1, bgcolor: "#f9f9f9" }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {note.userName} - {formatDate(note.createdAt)}
                        </Typography>
                        <Typography variant="body2">{note.text}</Typography>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    No notes
                  </Typography>
                )}
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    size="small"
                    label="Add Note"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    multiline
                    rows={2}
                    fullWidth
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleAddNote}
                    disabled={!newNote.trim()}
                  >
                    Add
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Assign Dialog */}
        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
        >
          <DialogTitle>Assign Error</DialogTitle>
          <DialogContent>
            <FormControl
              fullWidth
              sx={{ mt: 2 }}
            >
              <InputLabel id="assign-user-label">Assign To</InputLabel>
              <Select
                labelId="assign-user-label"
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="Assign To"
              >
                {users.length === 0 && (
                  <MenuItem disabled>No users available</MenuItem>
                )}
                {users.map((user) => (
                  <MenuItem
                    key={user.id || user._id}
                    value={user.id || user._id}
                  >
                    {user.legalName || user.name || user.username} (
                    {user.id || user._id})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitAssignment}
              variant="contained"
              disabled={!selectedUser}
            >
              Assign
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resolve Confirmation Dialog */}
        <Dialog_YesNo
          open={resolveDialogOpen}
          onClose={() => setResolveDialogOpen(false)}
          title="Resolve Error"
          message="Mark this error as resolved?"
          onYes={confirmResolveError}
          onNo={() => {
            setResolveDialogOpen(false);
            setErrorToResolve(null);
          }}
        />

        <Dialog_YesNo
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title="Delete Error Log"
          message="Delete this error log? This action cannot be undone."
          onYes={confirmDeleteError}
          onNo={() => {
            setDeleteDialogOpen(false);
            setErrorToDelete(null);
          }}
        />
      </Box>
    </Page>
  );
};

export default ErrorLogs;
